import React, { useState, useEffect } from 'react';
import { Order } from '../types';
import { ordersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button, Badge, TabNavigation } from './ui';
import toast from 'react-hot-toast';

const KitchenOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'mutfak' | 'tamamlanmis'>('mutfak');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const { user, logout } = useAuth();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    loadOrders();
    // Her 30 saniyede bir siparişleri yenile
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadOrders = async () => {
    try {
      
      // Token kontrolü
      const token = localStorage.getItem('token');
      if (!token) {
        // console.error('❌ Token bulunamadı');
        toast.error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        logout();
        return;
      }
      
      // Token süresini kontrol et
      try {
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        if (tokenData.exp && tokenData.exp < currentTime) {
          // console.error('❌ Token süresi dolmuş');
          toast.error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
          logout();
          return;
        }
      } catch (error) {
        // console.error('❌ Token parse hatası:', error);
        toast.error('Geçersiz token. Lütfen tekrar giriş yapın.');
        logout();
        return;
      }
      
      // Mutfak için özel API fonksiyonu kullan
      const data = await ordersAPI.getKitchenOrders();
      // İptal edilen siparişleri filtrele
      const filteredOrders = data.filter(order => order.status !== 'iptal');
      setOrders(filteredOrders);
    } catch (error: any) {
      // console.error('❌ Mutfak siparişleri yüklenirken hata:', error);
      
      // Hata detaylarını göster
      if (error.response) {
        // console.error('Response status:', error.response.status);
        // console.error('Response data:', error.response.data);
        toast.error(`Siparişler yüklenemedi: ${error.response.status} - ${error.response.data?.error || 'Bilinmeyen hata'}`);
      } else if (error.request) {
        // console.error('Request error:', error.request);
        toast.error('Sunucuya bağlanılamadı. Backend çalışıyor mu?');
      } else {
        // console.error('Error:', error.message);
        toast.error(`Siparişler yüklenemedi: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const completeOrder = async (orderId: number) => {
    try {
      await ordersAPI.markReady(orderId);
      toast.success('Sipariş hazır olarak işaretlendi!');
      setSelectedOrder(null);
      await loadOrders(); // Listeyi yenile
    } catch (error: any) {
      // console.error('❌ Sipariş hazır işaretleme hatası:', error);
      
      // Hata detaylarını göster
      if (error.response) {
        // console.error('Response status:', error.response.status);
        // console.error('Response data:', error.response.data);
        toast.error(`Sipariş hazır işaretlenemedi: ${error.response.status} - ${error.response.data?.error || 'Bilinmeyen hata'}`);
      } else if (error.request) {
        // console.error('Request error:', error.request);
        toast.error('Sunucuya bağlanılamadı. Backend çalışıyor mu?');
      } else {
        // console.error('Error:', error.message);
        toast.error(`Sipariş hazır işaretlenemedi: ${error.message}`);
      }
    }
  };

  const getOrderTime = (createdAt: string) => {
    const now = new Date();
    const orderTime = new Date(createdAt);
    const diffMinutes = Math.floor((now.getTime() - orderTime.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Az önce';
    if (diffMinutes < 60) return `${diffMinutes} dk önce`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `${diffHours} saat ${diffMinutes % 60} dk önce`;
  };

  const getOrderTimeColor = (createdAt: string) => {
    const now = new Date();
    const orderTime = new Date(createdAt);
    const diffMinutes = Math.floor((now.getTime() - orderTime.getTime()) / (1000 * 60));
    
    if (diffMinutes < 5) return 'text-green-600';
    if (diffMinutes < 15) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cafe-brown mx-auto mb-4"></div>
          <p className="text-gray-600">Mutfak siparişleri yükleniyor...</p>
        </div>
      </div>
    );
  }

  const mutfakOrders = orders.filter(order => 
    // Mutfakta sekmesi: Sadece hazırlanması gereken siparişler
    order.status === 'mutfak' || 
    // Hızlı siparişler ödendikten sonra da mutfakta kalır (hazırlanıp verilecek)
    (order.status === 'ödendi' && (order.table_id === 0 || order.table_id === null))
  );

  const tamamlanmisOrders = orders.filter(order => 
    // Tamamlanan sekmesi: Hazır olan, ödeme bekleyen, tamamlanan siparişler
    ['hazır', 'ödeme_bekliyor', 'tamamlandı'].includes(order.status) || 
    // Masa siparişleri ödendikten sonra tamamlanan sekmesine gider
    (order.status === 'ödendi' && order.table_id !== null && order.table_id !== 0)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">🍳 Mutfak Siparişleri</h1>
              <p className="text-sm text-gray-600">Hoş geldiniz, {user?.username}</p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="info"
                size="sm"
                onClick={loadOrders}
              >
🔄 Yenile
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={logout}
              >
🚪 Çıkış
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TabNavigation
            tabs={[
              { key: 'mutfak', label: `🍽️ Hazırlanacak (${mutfakOrders.length})`, icon: '🍽️' },
              { key: 'tamamlanmis', label: `✅ Tamamlanan (${tamamlanmisOrders.length})`, icon: '✅' }
            ]}
            activeTab={activeTab}
            onTabChange={(tabKey: string) => setActiveTab(tabKey as 'mutfak' | 'tamamlanmis')}
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                 {activeTab === 'mutfak' && mutfakOrders.length === 0 && (
           <div className="text-center py-12">
             <div className="text-6xl mb-4">🍽️</div>
             <h3 className="text-xl font-semibold text-gray-600 mb-2">Tamamlanan Sipariş Yok</h3>
             <p className="text-gray-500">Şu anda tamamlanan sipariş bulunmuyor</p>
           </div>
         )}

                 {activeTab === 'tamamlanmis' && tamamlanmisOrders.length === 0 && (
           <div className="text-center py-12">
             <div className="text-6xl mb-4">✅</div>
             <h3 className="text-xl font-semibold text-gray-600 mb-2">Mutfakta Sipariş Yok</h3>
             <p className="text-gray-500">Şu anda mutfakta bekleyen sipariş bulunmuyor</p>
           </div>
         )}

        {/* Orders List */}
        <div className="space-y-4">
          {(activeTab === 'mutfak' ? mutfakOrders : tamamlanmisOrders).map((order) => (
            <div
              key={order.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center space-x-4">
                  {/* Order ID Circle */}
                  <div className="w-12 h-12 bg-cafe-brown text-white rounded-full flex items-center justify-center font-bold">
                    #{order.id}
                  </div>
                  
                  {/* Order Info */}
                  <div>
                                     <h3 className="text-lg font-semibold text-gray-800">
                       {order.table_id === 0 || order.table_id === null ? (
                         <span className="flex items-center gap-2">
                           🚀 Hızlı Sipariş
                           {order.status === 'ödendi' && (
                             <Badge variant="info">
                               ÖDENDİ - HAZIRLANIYOR
                             </Badge>
                           )}
                         </span>
                       ) : (
                         <span className="flex items-center gap-2">
                           🍽️ Masa {order.table?.table_number ? (order.table.table_number <= 18 ? `T${order.table.table_number}` : `C${order.table.table_number - 18}`) : 'N/A'}
                           {order.status === 'ödeme_bekliyor' && (
                             <Badge variant="warning">
                               💳 ÖDEME BEKLİYOR
                             </Badge>
                           )}
                         </span>
                       )}
                     </h3>
                    <p className="text-sm text-gray-600">
                      {order.user?.username} • {new Date(order.created_at).toLocaleTimeString('tr-TR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
                
                {/* Time and Amount */}
                <div className="text-right">
                  <div className={`text-sm font-medium ${getOrderTimeColor(order.created_at)}`}>
                    {getOrderTime(order.created_at)}
                  </div>
                                      <div className="text-xl font-bold text-cafe-brown dark:text-cafe-light">
                      {formatPrice(order.total_amount)}
                    </div>
                </div>
              </div>
              
              {/* Order Items */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {order.items?.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1">
                        <span className="font-medium text-gray-800">{item.product?.name}</span>
                        {item.notes && (
                          <p className="text-xs text-orange-600 mt-1 font-medium">📝 {item.notes}</p>
                        )}
                      </div>
                      <Badge variant="info" className="ml-2">
                        {item.quantity}x
                      </Badge>
                    </div>
                  ))}
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 mt-4">
                  <Button
                    variant="info"
                    size="sm"
                    onClick={() => setSelectedOrder(order)}
                  >
                    📋 Detay
                  </Button>
                                     {activeTab === 'mutfak' && (
                     order.status === 'mutfak' || 
                     (order.status === 'ödendi' && (order.table_id === 0 || order.table_id === null))
                   ) && (
                     <Button
                       variant="success"
                       size="sm"
                       onClick={() => completeOrder(order.id)}
                     >
                       ✅ Hazır
                     </Button>
                   )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Auto Refresh Info */}
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">
          🔄 Sayfa otomatik olarak 30 saniyede bir yenilenir
        </p>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                                 <h2 className="text-2xl font-bold text-gray-800">
                   {selectedOrder.table_id === 0 || selectedOrder.table_id === null ? (
                     <span className="flex items-center gap-2">
                       🚀 Hızlı Sipariş
                       {selectedOrder.status === 'ödendi' && (
                         <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                           ÖDENDİ - HAZIRLANIYOR
                         </span>
                       )}
                     </span>
                  ) : (
                    `🍽️ Masa ${selectedOrder.table?.table_number ? (selectedOrder.table.table_number <= 18 ? `T${selectedOrder.table.table_number}` : `C${selectedOrder.table.table_number - 18}`) : 'N/A'}`
                  )} - Sipariş #{selectedOrder.id}
                 </h2>
                <p className="text-gray-600">
                  {new Date(selectedOrder.created_at).toLocaleString('tr-TR')}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedOrder(null)}
                className="text-2xl"
              >
                ×
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg mb-3">Sipariş Öğeleri</h3>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div>
                        <h4 className="font-medium">{item.product?.name}</h4>
                        {item.notes && (
                          <p className="text-sm text-orange-600 font-medium">📝 {item.notes}</p>
                        )}
                        {item.added_by_user && (
                          <p className="text-sm text-blue-600">👤 {item.added_by_user.username}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{item.quantity}x</div>
                        <div className="text-sm text-gray-600">{formatPrice(item.unit_price)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Toplam:</span>
                  <span className="text-2xl font-bold text-cafe-brown">{formatPrice(selectedOrder.total_amount)}</span>
                </div>
              </div>

                             {(selectedOrder.status === 'mutfak' || 
                 (selectedOrder.status === 'ödendi' && (selectedOrder.table_id === 0 || selectedOrder.table_id === null))) && (
                 <div className="border-t pt-4">
                   <Button
                     variant="success"
                     size="lg"
                     fullWidth
                     onClick={() => completeOrder(selectedOrder.id)}
                   >
                     ✅ Siparişi Hazır Olarak İşaretle
                   </Button>
                 </div>
               )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KitchenOrders;
